
import { useCallback, useState, useMemo, useEffect, useRef } from "react"; // Added useRef
import moment from "moment-timezone";
import { create } from "zustand";
import { FEATURE_GROUPS, PossibleRole, doesUserHavePackages, doesUserPackageHavePermissionTypes, getUserPackages, getUserRole } from "lhc_permissions";
import { GenerateLoadingOrError } from "../../lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../../components/ui/dialog";
import { Combobox } from "../../components/ui/combobox";
import { Button } from "../../components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { hasAccessToFeature } from "@/lib/models/auth";
import {
  useListMobileResources,
  useCreateResource,
  useUpdateResource,
  useGenerateResourceUrl,
  UpdateResourceValidation,
  CreateResourceValidation, // This validation will still be used for the data sent to API
  UpdateResourceResponse,
  CreateResourceResponse,
  ClientResource,
} from "@/lib/api/mobile";
import useDebounce from "../otc/useDebounce";
import { Loader } from "@/components/ui/loader";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { DataTable, DataTableProps, } from "@/components/ui/virtualizedTable";
import { Checkbox } from "@/components/ui/checkbox";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form"; // Added Controller
import { zodResolver } from "@hookform/resolvers/zod";
import { UploadPresignedFile } from "@/lib/file"; // Import UploadPresignedFile

// --- Helper: Define File Types ---
const VALID_FILE_TYPES = [".pdf", ".png", ".jpeg", ".jpg", ".webp"] as const;
type FileType = typeof VALID_FILE_TYPES[number];

// Zod schema for the form, including the client-side file object
const CreateResourceFormValidation = CreateResourceValidation.extend({
  actual_file: z.custom<File>((val) => val instanceof File, "Please select a file")
});

// --- CreateResourceDialog ---
interface CreateResourceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function CreateResourceDialog({ isOpen, onClose, onSuccess }: CreateResourceDialogProps) {
  const { toast } = useToast();
  const createResourceMutation = useCreateResource();
  const fileInputRef = useRef<HTMLInputElement>(null);

  type FormData = z.infer<typeof CreateResourceFormValidation>;

  const { register, handleSubmit, reset, control, setValue, watch, setError, clearErrors, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(CreateResourceFormValidation),
    defaultValues: {
      filename: "",
      display_name: "",
      // file_type is derived, not set by default here
      is_enabled: true,
      actual_file: undefined,
    }
  });

  const selectedFile = watch("actual_file");

  useEffect(() => {
    if (selectedFile) {
      const fileName = selectedFile.name;
      const fileExtension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();

      if (VALID_FILE_TYPES.includes(fileExtension as FileType)) {
        setValue("filename", fileName); // Prefill filename
        setValue("file_type", fileExtension as FileType);
        clearErrors("file_type");
        clearErrors("actual_file"); // Clear general file error if type is now valid
      } else {
        setValue("file_type", "" as any); // Reset or set to an invalid state
        setError("actual_file", { type: "manual", message: `Invalid file type. Allowed: ${VALID_FILE_TYPES.join(", ")}` });
      }
    } else {
      setValue("filename", "");
      setValue("file_type", "" as any); // Or a default/placeholder
    }
  }, [selectedFile, setValue, setError, clearErrors]);


  const onSubmit = async (data: FormData) => {
    if (!data.actual_file) {
      toast({ title: "Error", description: "No file selected.", variant: "destructive" });
      return;
    }

    // Ensure file_type was correctly derived and is valid before proceeding
    if (!data.file_type || !VALID_FILE_TYPES.includes(data.file_type as FileType)) {
      setError("actual_file", { type: "manual", message: `Invalid file type. Allowed: ${VALID_FILE_TYPES.join(", ")}` });
      toast({ title: "Error", description: `Invalid file type. Allowed: ${VALID_FILE_TYPES.join(", ")}`, variant: "destructive" });
      return;
    }

    try {
      // 1. Create resource record
      const createPayload = {
        filename: data.filename,
        display_name: data.display_name,
        file_type: data.file_type as FileType, // Cast because it's validated
        is_enabled: data.is_enabled,
      };

      const createApiResult = await createResourceMutation(createPayload);

      if (!createApiResult.success || !createApiResult.data?.upload_url) {
        throw new Error("Failed to create resource record or get upload URL.");
      }

      toast({ title: "Step 1/2 Complete", description: "Resource record created. Uploading file..." });

      const uploadResult = await UploadPresignedFile(createApiResult.data.upload_url, data.actual_file, data.actual_file.type);

      if (!uploadResult.success) {
        throw new Error("File upload failed");
      }

      toast({ title: "Success (Step 2/2)", description: "Resource created and file uploaded successfully." });
      onSuccess();
      reset(); // Reset form fields
      if (fileInputRef.current) fileInputRef.current.value = ""; // Clear file input
      onClose();

    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Could not create resource.", variant: "destructive" });
    }
  };


  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        reset();
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
      onClose();
    }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New Mobile Resource</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="actual_file_input">Resource File</Label>
            <Controller
              name="actual_file"
              control={control}
              render={({ field: { onChange, onBlur, name, ref } }) => (
                <Input
                  id="actual_file_input"
                  type="file"
                  ref={fileInputRef} // Use the new ref here
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    onChange(file || undefined);
                    if (file) {
                      const fileName = file.name;
                      const fileExtension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
                      if (VALID_FILE_TYPES.includes(fileExtension as FileType)) {
                        setValue("filename", fileName, { shouldValidate: true });
                        setValue("display_name", fileName.substring(0, fileName.lastIndexOf('.'))); // Pre-fill display name without extension
                        setValue("file_type", fileExtension as FileType, { shouldValidate: true });
                        clearErrors("actual_file");
                      } else {
                        setValue("filename", fileName); // Still set filename for potential error msg
                        setValue("file_type", "" as any, { shouldValidate: true }); // Invalid type
                        setError("actual_file", { type: "manual", message: `Invalid file type. Allowed: ${VALID_FILE_TYPES.join(", ")}` });
                      }
                    } else { // No file selected or selection cancelled
                      setValue("filename", "", { shouldValidate: true });
                      setValue("display_name", "", { shouldValidate: true });
                      setValue("file_type", "" as any, { shouldValidate: true });
                    }
                  }}
                  onBlur={onBlur}
                />
              )}
            />
            {errors.actual_file && <p className="text-red-500 text-sm">{errors.actual_file.message}</p>}
            {errors.file_type && !errors.actual_file && <p className="text-red-500 text-sm">{errors.file_type.message}</p>} {/* Show general file_type error if not an actual_file error */}
          </div>
          {/* Filename is now derived and shown, can be read-only or removed if display_name is sufficient */}
          <div>
            <Label htmlFor="filename_display">File Name</Label>
            <Input id="filename_display" {...register("filename")} readOnly className="bg-gray-100" />
            {errors.filename && <p className="text-red-500 text-sm">{errors.filename.message}</p>}
          </div>
          <div>
            <Label htmlFor="display_name">Display Name</Label>
            <Input id="display_name" {...register("display_name")} />
            {errors.display_name && <p className="text-red-500 text-sm">{errors.display_name.message}</p>}
          </div>
          {/* File Type is now derived, can be read-only or hidden */}
          <div>
            <Label htmlFor="file_type_display">File Type</Label>
            <Input id="file_type_display" {...register("file_type")} readOnly className="bg-gray-100" />
            {/* errors.file_type is handled by actual_file error message */}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => {
              reset();
              if (fileInputRef.current) fileInputRef.current.value = "";
              onClose();
            }} disabled={isSubmitting}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting || !watch("actual_file")}>
              {isSubmitting ? "Uploading" : null}
              {isSubmitting ? null : "Create & Upload"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface UpdateResourceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  resource: ClientResource | null;
  onSuccess: () => void;
}

function UpdateResourceDialog({ isOpen, onClose, resource, onSuccess }: UpdateResourceDialogProps) {
  const { toast } = useToast();
  const updateResourceMutation = useUpdateResource();

  type FormData = z.infer<typeof UpdateResourceValidation>;

  const { register, handleSubmit, reset, setValue, formState: { errors, isSubmitting }, control } = useForm<FormData>({
    resolver: zodResolver(UpdateResourceValidation),
  });

  useEffect(() => {
    if (resource) {
      setValue("id", resource.id);
      setValue("display_name", resource.display_name);
      setValue("is_enabled", resource.is_enabled);
    } else {
      reset({ id: undefined, display_name: "", is_enabled: true });
    }
  }, [resource, setValue, reset]);

  if (!resource) return null;

  const onSubmit = async (data: FormData) => {
    try {
      const result = await updateResourceMutation(data);
      if (result.success && result.data) {
        toast({ title: "Success", description: "Resource updated successfully." });
        onSuccess();
        onClose();
      } else {
        throw new Error("Failed to update resource");
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Could not update resource.", variant: "destructive" });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Update Mobile Resource</DialogTitle>
          <DialogDescription>Filename: {resource.filename} ({resource.file_type})</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <input type="hidden" {...register("id")} />
          <div>
            <Label htmlFor="update_display_name">Display Name</Label>
            <Input id="update_display_name" {...register("display_name")} />
            {errors.display_name && <p className="text-red-500 text-sm">{errors.display_name.message}</p>}
          </div>
          <div className="flex items-center space-x-2">
            <Controller
              name="is_enabled"
              control={control}
              defaultValue={resource.is_enabled}
              render={({ field }) => (
                <Checkbox
                  id="update_is_enabled"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  ref={field.ref}
                />
              )}
            />
            <Label htmlFor="update_is_enabled">Enabled</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Updating" : null}
              {isSubmitting ? null : "Update"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


interface PreviewResourceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  resourceName: string | undefined;
  previewUrl: string | null;
  isLoading: boolean;
  fileType: string | undefined;
}
function PreviewResourceDialog({ isOpen, onClose, resourceName, previewUrl, isLoading, fileType }: PreviewResourceDialogProps) {
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[80vw] md:max-w-[80vw] lg:max-w-[70vw] h-[70vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Preview: {resourceName || "Resource"}</DialogTitle>
          <DialogClose onClick={onClose} />
        </DialogHeader>
        <div className="flex-grow flex items-center justify-center">
          {isLoading && <Loader className="h-10 w-10 animate-spin" />}
          {!isLoading && !previewUrl && <p>Could not load preview.</p>}
          {!isLoading && previewUrl && (
            (fileType === ".pdf") ? (
              <iframe src={previewUrl} title={resourceName} width="100%" height="100%" />
            ) : (VALID_FILE_TYPES.includes(fileType as FileType || "") && ['.png', '.jpeg', '.jpg', '.webp'].includes(fileType || "")) ? (
              <img src={previewUrl} alt={resourceName} className="max-w-full max-h-full object-contain" />
            ) : (
              <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                Open preview in new tab
              </a>
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


export function MobileResourcesDashboard() {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [currentResource, setCurrentResource] = useState<ClientResource | null>(null);

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [resourceForPreview, setResourceForPreview] = useState<ClientResource | null>(null);

  const { toast } = useToast();
  const generateUrlMutation = useGenerateResourceUrl();
  const listResourcesRes = useListMobileResources(); // To force refresh after C/U

  const handleCreateSuccess = () => {
    listResourcesRes.refetch(); // Refetch list after creating
  };

  const handleUpdateSuccess = () => {
    listResourcesRes.refetch(); // Refetch list after updating
  };

  const handleRowClick = useCallback((resource: ClientResource) => {
    setCurrentResource(resource);
    setIsUpdateOpen(true);
  }, []);

  const handlePreview = useCallback(async (resource: ClientResource) => {
    setResourceForPreview(resource);
    setIsPreviewLoading(true);
    setIsPreviewModalOpen(true);
    setPreviewUrl(null);
    try {
      const result = await generateUrlMutation({ id: resource.id });
      if (result.success && result.data?.link) {
        setPreviewUrl(result.data.link);
      } else {
        throw new Error("Failed to generate preview URL");
      }
    } catch (error: any) {
      toast({ title: "Preview Error", description: error.message || "Could not generate preview link.", variant: "destructive" });
      setIsPreviewModalOpen(false); // Close modal on error
    } finally {
      setIsPreviewLoading(false);
    }
  }, [generateUrlMutation, toast]);

  return (
    <>
      <div className="container mx-auto py-4 px-6 flex flex-col mb-2">
        <div className="flex justify-between items-center mb-4">
          <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
            Manage Mobile Resources
          </h1>
          <Button onClick={() => setIsCreateOpen(true)}>Create Resource</Button>
        </div>
        <div className="flex items-start flex-col h-full mt-4">
          <ResourcesList
            onRowClick={handleRowClick}
            onPreview={handlePreview}
          />
        </div>
      </div>

      <CreateResourceDialog
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSuccess={handleCreateSuccess}
      />
      <UpdateResourceDialog
        isOpen={isUpdateOpen}
        onClose={() => setIsUpdateOpen(false)}
        resource={currentResource}
        onSuccess={handleUpdateSuccess}
      />
      <PreviewResourceDialog
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        resourceName={resourceForPreview?.display_name}
        previewUrl={previewUrl}
        isLoading={isPreviewLoading}
        fileType={resourceForPreview?.file_type}
      />
    </>
  );
}

interface ResourcesListProps {
  onRowClick: (resource: ClientResource) => void;
  onPreview: (resource: ClientResource) => void;
}

export function ResourcesList({ onRowClick, onPreview }: ResourcesListProps) {
  const listResourcesRes = useListMobileResources();
  const [searchTerm, setSearchTerm] = useState("");
  const [fileTypeFilter, setFileTypeFilter] = useState<FileType | "">("");
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const allResources = listResourcesRes?.data?.data || [];

  const filteredAndSortedResources = useMemo(() => {
    let resources = [...allResources];

    // Filter by search term (display_name or filename)
    if (debouncedSearchTerm) {
      resources = resources.filter(r =>
        r.display_name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        r.filename.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      );
    }

    // Filter by file type
    if (fileTypeFilter) {
      resources = resources.filter(r => r.file_type === fileTypeFilter);
    }

    // Sort: enabled first, then by display_name
    resources.sort((a, b) => {
      if (a.is_enabled !== b.is_enabled) {
        return a.is_enabled ? -1 : 1; // true (enabled) comes before false (disabled)
      }
      return a.display_name.localeCompare(b.display_name); // secondary sort by name
    });

    return resources;
  }, [allResources, debouncedSearchTerm, fileTypeFilter]);

  const resourcesColumns: any = useMemo(() => [
    {
      header: "Display Name",
      accessorKey: "display_name",
      cell: ({ row }: any) => <div>{row.original.display_name}</div>,
      enableColumnFilter: false,
    },
    {
      header: "Filename",
      accessorKey: "filename",
      cell: ({ row }: any) => <div>{row.original.filename}</div>,
      enableColumnFilter: false,
    },
    {
      header: "File Type",
      accessorKey: "file_type",
      cell: ({ row }: any) => <div>{row.original.file_type}</div>,
      enableColumnFilter: false,
    },
    {
      header: "Enabled",
      accessorKey: "is_enabled",
      cell: ({ row }: any) => <div>{row.original.is_enabled ? "Yes" : "No"}</div>,
      enableColumnFilter: false,
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }: any) => (
        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); onPreview(row.original); }}>
          Preview
        </Button>
      ),
      enableColumnFilter: false,
    }
  ], [onPreview]);

  const loadingOrErrorUi = GenerateLoadingOrError([listResourcesRes]);
  if (loadingOrErrorUi) return loadingOrErrorUi;


  return (
    <div className="container mx-auto w-full">
      <div className="flex space-x-4 mb-4">
        <Input
          placeholder="Search by name or filename..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
        />
        <Combobox
          value={fileTypeFilter}
          onValueChange={(value: string) => setFileTypeFilter(value as FileType | "")}
          options={[
            { value: "", label: "All Types" },
            ...VALID_FILE_TYPES.map(type => ({ value: type, label: type }))
          ]}
          placeholder="Filter by file type..."
          className="md:w-[250px]"
        />
      </div>
      <DataTable
        columns={resourcesColumns}
        data={filteredAndSortedResources}
        height="500px" // Adjust as needed
        onRowClick={onRowClick} // Assuming DataTable supports this prop
      />
    </div>
  )
}
